import { observer } from 'mobx-react-lite';
import Image from 'next/image';
import { useEffect, useState } from 'react';

import Button from '@/components/common/Button/Button';
import DiscountProgressBar from '@/components/common/DiscountProgressBar/DiscountProgressBar';
import { useScrollDirection } from '@/hooks/useScrollDirection';
import { useStore } from '@/models/root-store';
import { Mode } from '@/utils/constants';

type ViewCartContainerProps = {
  hideCart?: boolean;
  hideDiscountProgressBar?: boolean;
  showBulkDiscountProgressBarOnMobile?: boolean;
  // scrollDirection: ScrollDirection;
};
const getPaddingClass = (isHidden: boolean, showMenuBar: boolean) => {
  const basePadding = 'lg:pt-5';
  if (isHidden) {
    return `pt-3 ${basePadding}`;
  }
  if (!showMenuBar) {
    return `md:pt-[74px] ${basePadding}`;
  }
  return `pt-[74px] ${basePadding}`;
};
const ViewCartContainer: React.FC<ViewCartContainerProps> = observer(
  // @ts-ignore
  ({
    hideCart,
    hideDiscountProgressBar = false,
    showBulkDiscountProgressBarOnMobile = false,
  }) => {
    const {
      cartStore: { cart, cartToUse },
      productStore: {
        filters: { openFilter, filterCount },
      },
    } = useStore();
    const scrollDirection = useScrollDirection();

    const { openCart, isCartOpen } = cart;
    const { numberOfItems, subTotal, mode, menuBuilderPlan } = cartToUse;

    const [isHidden, setIsHidden] = useState(true);
    const [initLoad, setInitLoad] = useState(true);

    useEffect(() => {
      if (!initLoad) setIsHidden(false);
      else setInitLoad(false);
    }, [menuBuilderPlan?.numberOfDiscountedItems]);

    useEffect(() => {
      if (!initLoad) setIsHidden(scrollDirection !== 'down');
      else setInitLoad(false);
    }, [scrollDirection]);

    useEffect(() => {
      setIsHidden(true);
      setInitLoad(true);
    }, []);

    const hideOnMobile = getPaddingClass(
      isHidden,
      showBulkDiscountProgressBarOnMobile
    );
    const buttonLabel =
      mode === Mode.EDIT_SUBSCRIPTION ? 'View Items' : 'View Cart';
    const style = mode === Mode.EDIT_SUBSCRIPTION ? '' : 'hidden lg:block';

    return (
      !isCartOpen && (
        <div
          className={`sticky bottom-0 z-20 bg-white shadow-lg`}
          style={{
            boxShadow: '4px -8px 15px 0px rgba(0, 0, 0, 0.05)',
          }}
        >
          <div
            className={`container relative z-10 flex flex-col p-3 md:px-8 lg:flex-row lg:items-center lg:justify-between lg:py-5 ${hideOnMobile} transition-all duration-500 ease-in-out`}
          >
            <div
              className={`absolute inset-3 mb-6 w-auto max-w-full justify-between md:inset-x-8 lg:relative lg:inset-0 lg:mb-0 lg:flex lg:w-3/5`}
            >
              {!hideDiscountProgressBar && <DiscountProgressBar view="large" />}
            </div>
            <div className="z-20 flex justify-between space-x-3 bg-white">
              <Button
                className="w-1/2 px-2 sm:px-4 lg:hidden"
                theme="tertiary"
                onClick={openFilter}
              >
                <div className="flex items-center justify-center space-x-2 text-sm md:text-base">
                  <div>
                    <Image
                      src="https://www.datocms-assets.com/81001/1706829984-tune.svg"
                      width={15}
                      height={15}
                      alt="filter"
                    />
                  </div>
                  <span className="font-interSemibold">
                    Filters ({filterCount})
                  </span>
                </div>
              </Button>
              {!hideCart && (
                <Button
                  theme="septenary"
                  className="w-1/2 lg:w-80 lg:py-2"
                  onClick={openCart}
                >
                  <div className="flex w-full items-center justify-center px-2 lg:justify-between">
                    {mode !== Mode.EDIT_SUBSCRIPTION && (
                      <span className="flex shrink-0 pr-2 lg:hidden">
                        <Image
                          src="https://www.datocms-assets.com/81001/1707261189-cart.svg"
                          width={20}
                          height={20}
                          alt="cart"
                        />
                        <span className="font-interSemibold text-sm">
                          ({numberOfItems})
                        </span>
                      </span>
                    )}
                    <span
                      className={`${style} font-interMedium text-sm md:text-base`}
                    >
                      {buttonLabel} ({numberOfItems})
                    </span>
                    {mode !== Mode.EDIT_SUBSCRIPTION && (
                      <span
                        className={'font-interSemibold text-sm lg:text-2xl'}
                      >
                        ${subTotal.toFixed(2)}
                      </span>
                    )}
                  </div>
                </Button>
              )}
            </div>
          </div>
        </div>
      )
    );
  }
);

export default ViewCartContainer;
